/**
 * What Next gave each surface for free and the router did not.
 *
 * Next mounted every `error.tsx` as a boundary around its own segment, so a
 * render throw cost one panel and left the sidebar to navigate away from. Under
 * the router the same throw reached the root and unmounted the document: a white
 * page whose only exit is a reload. This is the mechanism that puts those files
 * back to work — the views themselves are unchanged, and none was written here.
 *
 * It lives under `~/core` rather than in the Agent because both modules throw.
 * The Agent had it from 2026-09-12 and Analytics, the shell and the common
 * screens did not, which is the asymmetry that made a bad date on one card cost
 * the whole document.
 *
 * The error is dropped when the location changes, because Next discarded a
 * segment's boundary state when the segment did. Compared rather than keyed:
 * keying the subtree on the path would remount every healthy route on every
 * navigation, which is a much larger change than the one this is fixing.
 */

import { createLogger } from "@ondit/logger";
import { Component, type ComponentType, type ErrorInfo, type ReactNode } from "react";
import { useLocation } from "react-router";

const logger = createLogger("RouteBoundary");

/** What a fallback is handed. Structurally Next's `error.tsx` props, which is
 *  what the Agent's own fallbacks are still typed as. */
export interface BoundaryFallbackProps {
	error: Error & { digest?: string };
	reset: () => void;
}

type Fallback = ComponentType<BoundaryFallbackProps>;

interface RouteBoundaryProps {
	fallback: Fallback;
	children: ReactNode;
}

interface CaughtProps extends RouteBoundaryProps {
	pathname: string;
}

interface CaughtState {
	error: (Error & { digest?: string }) | null;
}

class Caught extends Component<CaughtProps, CaughtState> {
	public state: CaughtState = { error: null };

	public static getDerivedStateFromError(error: Error & { digest?: string }): CaughtState {
		return { error };
	}

	/**
	 * The component stack is the one part of the failure only the boundary is
	 * handed: the fallback logs the message and the digest it was given, and
	 * neither says which component threw.
	 */
	public componentDidCatch(error: Error, info: ErrorInfo) {
		logger.error("Route crashed", {
			pathname: this.props.pathname,
			name: error.name,
			message: error.message,
			componentStack: info.componentStack,
		});
	}

	public componentDidUpdate(previous: CaughtProps) {
		if (this.state.error && previous.pathname !== this.props.pathname) this.setState({ error: null });
	}

	public render() {
		const { error } = this.state;
		if (!error) return this.props.children;
		const Fallback = this.props.fallback;
		return <Fallback error={error} reset={() => this.setState({ error: null })} />;
	}
}

export function RouteBoundary({ fallback, children }: RouteBoundaryProps) {
	return (
		<Caught fallback={fallback} pathname={useLocation().pathname}>
			{children}
		</Caught>
	);
}
